import { AppContext, getAppContext } from 'src/context/AppContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import useDeepCompareEffect from 'use-deep-compare-effect'

export type FetchFromAppContext<T, Input> = (
    ctx: AppContext,
    input: Input
) => Promise<T>

const isPrimitive = (val: any) => val == null || /^[sbn]/.test(typeof val)

const getHook = (val: any) =>
    isPrimitive(val) ? useEffect : useDeepCompareEffect

export const useContextFetch = <T, Input>(
    doFetch: FetchFromAppContext<T, Input>,
    input: Input
): T | null => {
    const [val, set] = useState<T | null>(null)

    const ctx = getAppContext()

    getHook(input)(() => {
        doFetch(ctx, input).then(set)
    }, [input])

    return val
}

export const useContextFetcher = <T, Input>(
    fetcher: (ctx: AppContext) => (i: Input) => Promise<T>,
    input?: Input
): T | null => {
    const [val, set] = useState<T | null>(null)

    const ctx = getAppContext()

    getHook(input)(() => {
        // @ts-ignore could be undefined - no args fun
        fetcher(ctx)(input).then(set)
    }, [input])

    return val
}

export const useContextRefetcher = <T, Input = void>(
    fetcher: (ctx: AppContext) => (i: Input) => Promise<T>,
    input?: Input
): [T | null, () => void] => {
    const [val, set] = useState<T | null>(null)
    const ctx = getAppContext()
    const fetcherRef = useRef(fetcher)
    useEffect(() => {
        fetcherRef.current = fetcher
    }, [fetcher])

    const doFetch = useCallback(() => {
        return fetcherRef
            .current(ctx)(input as Input)
            .then(res => {
                set(res)
                return res
            })
            .catch(() => {})
    }, [ctx, input])

    getHook(input)(() => {
        let mounted = true
        fetcherRef
            .current(ctx)(input as Input)
            .then(res => {
                if (mounted) set(res)
            })
            .catch(() => {})
        return () => {
            mounted = false
        }
    }, [input])

    // Stable, fire-and-forget refetch for callers
    const refetch = useCallback(() => {
        doFetch()
    }, [doFetch])

    return [val, refetch]
}

export const createUseContextObjectFetcher =
    <CtxObj>(get: (ctx: AppContext) => CtxObj) =>
    <T, Input>(
        fetcher: (ctxObj: CtxObj) => (input: Input) => Promise<T>,
        input?: Input
    ): T | null =>
        useContextFetcher<T, Input>(ctx => fetcher(get(ctx)), input)
